import { useCallback, useEffect, useRef, useState, type MouseEvent, type PointerEvent } from "react";

/** Horizontal travel, in px, that counts as a swipe rather than a tap. */
const SWIPE_PX = 40;

function prefersReducedMotion(): boolean {
  return typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches === true;
}

export interface CarouselOptions {
  /** Advance on its own every this many ms; 0 or undefined for never. */
  autoplayMs?: number;
  /** Pauses autoplay while true, e.g. when the pointer is away. */
  paused?: boolean;
}

/**
 * Index and gestures for a photo carousel: wrap-around previous/next,
 * swipes, and optional autoplay. Autoplay stops for good once the shopper
 * navigates themselves, and never runs for people who prefer reduced motion.
 */
export function useCarousel(count: number, { autoplayMs, paused = false }: CarouselOptions = {}) {
  const [index, setIndex] = useState(0);
  const [stopped, setStopped] = useState(false);
  const start = useRef<{ x: number; y: number } | null>(null);
  const swiped = useRef(false);

  // A shorter photo list (e.g. another product) must not leave us past the end.
  const current = count > 0 ? Math.min(index, count - 1) : 0;

  const show = useCallback(
    (next: number, byShopper = true) => {
      if (count < 2) return;
      if (byShopper) setStopped(true);
      setIndex(((next % count) + count) % count);
    },
    [count],
  );

  const reset = useCallback(() => {
    setIndex(0);
    setStopped(false);
  }, []);

  useEffect(() => {
    if (!autoplayMs || paused || stopped || count < 2 || prefersReducedMotion()) return;
    const timer = setInterval(() => setIndex((i) => (i + 1) % count), autoplayMs);
    return () => clearInterval(timer);
  }, [autoplayMs, paused, stopped, count]);

  const swipeHandlers = {
    onPointerDown: (e: PointerEvent) => {
      swiped.current = false;
      start.current = { x: e.clientX, y: e.clientY };
    },
    onPointerUp: (e: PointerEvent) => {
      const from = start.current;
      start.current = null;
      if (!from || count < 2) return;
      const dx = e.clientX - from.x;
      if (Math.abs(dx) < SWIPE_PX || Math.abs(dx) < Math.abs(e.clientY - from.y)) return;
      swiped.current = true;
      show(current + (dx < 0 ? 1 : -1));
    },
    onPointerCancel: () => {
      start.current = null;
    },
    // A swipe over a link would otherwise also open it.
    onClickCapture: (e: MouseEvent) => {
      if (!swiped.current) return;
      swiped.current = false;
      e.preventDefault();
      e.stopPropagation();
    },
  };

  return {
    index: current,
    show,
    next: () => show(current + 1),
    prev: () => show(current - 1),
    reset,
    swipeHandlers,
  };
}
